import { useContext, useEffect, useState } from "react";
import axios from "axios";

import { useLocation } from "react-router-dom";
import { HostInfoContext } from "../../hooks/HostInfoContext.js";
import { Quotes } from "./Quotes.jsx";
import { NotesMethodology } from "./NotesMethodology.jsx";
import ChartTable from "../Chart/ChartTable.jsx";
import ChartPie from "../Chart/ChartPie.jsx";
import { getDuration, RenderCards } from "../Stories/Stories.jsx";
import { LinkDD, LinkSimple } from "../Stories/Links.js";
import { AlignmentMeters } from "./AlignmentMeters.jsx";
import SliderSankey from "../Chart/SliderSankey.jsx";
import { StoryHero } from "./StoryHero.jsx";
import { OrganizationAlignment } from "./OrganizationAlignment.jsx";
import { LegislatorOrgAlignmentTable } from "./LegislatorOrgAlignmentTable.jsx";
import { UtilityBar } from "../UtilityBar/UtilityBar.jsx";
import { ArrowContainer, Popover } from "react-tiny-popover";
import { QuestionMarkIcon } from "../TipSheetIcons/question-mark.jsx";

import { useHttpErrorHandler } from "../../hooks/useHttpErrorHandler";

import styled from "styled-components";
import redX from "../../assets/images/red-x.svg";
import greenCheck from "../../assets/images/green-check.svg";
import grayDash from "../../assets/images/gray-dash.svg";

const StoryWrapper = styled.div`
  padding: 4rem;
  background-color: #ffffff;

  @media (max-width: 1440px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const StoryContainer = styled.div`
  max-width: 2200px;
  margin: 0 auto;
`;

const SectionHeading = styled.h3`
  font-size: 1.875rem;
  font-weight: 700;
  line-height: 1.1;
  margin-block-end: 1.5rem;
  margin-block-start: 2rem;
`;

const Tooltip = styled.div`
  background: #d9d9d9;
  padding: 1rem;
  max-width: 300px;
  border-radius: 0.25rem;
  margin-block-end: -1px;
  font-size: 0.625rem;
  font-style: italic;
  line-height: 1.7;
`;

const TooltipButton = styled.button`
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
  border: none;
  padding: 0;
  background: none;
  margin-inline-start: 0.5rem;
`;

const GrayWrapper = styled.div`
  padding: 4rem;
  background-color: #fafafa;
  text-align: center;

  @media (max-width: 1440px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const GrayContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;

  hr {
    margin: 1.5rem 0;
    border: 0;
    border-top: 1px solid #cccccc;
  }

  a {
    color: #0a819f;

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
`;

const QuoteIntro = styled.p`
  color: #666666;
  margin-block-start: 0;
`;

const QuouteHeading = styled.h3`
  font-size: 1.25rem;
  line-height: 1.3;
`;

const ScrollAnchor = styled.div`
  /* offset enough room for the fixed utility bar */
  scroll-margin-top: 80px;
`;

const RenderCardsWrapper = styled.div`
  margin-block-start: 2rem;
`;

const WarningBox = styled.div`
  background: #fff4e5;
  border-left: 4px solid #f1c40f;
  padding: 1rem;
  border-radius: 0.25rem;
  margin-block-end: 1.5rem;
  line-height: 1.4;
`;

// a number of fixes mainly in the text narrative of phenoms
// should be ultimately done during phenom generation
const temp_fixes = [
  ["Democrat members", "Democratic members"],
  ["Democrat party", "Democratic party"],
  ["good questions", "questions"],
];

// function that applies above replacements
function replaceTempFixes(inString) {
  let newString = inString;
  temp_fixes?.forEach((replacement) => {
    newString = newString.replace(replacement[0], replacement[1]);
  });
  return newString;
}

// replaces legislator codes like __755 with actual first and last names from personas
// returns the modified string
export function replaceLegCodes(inString, personas) {
  const replaceList = inString.match(/__[0-9]+/g);
  let new_version = replaceTempFixes(inString);
  replaceList?.forEach((item) => {
    const leg_id = Number(item.substring(2));
    personas.forEach((persona) => {
      if (leg_id === persona["pid"]) {
        new_version = new_version.replace(
          item,
          LinkDD(
            persona["first"].trim() + " " + persona["last"].trim(),
            leg_id,
            "leg",
            leg_id,
            true,
          ),
        );
      }
    });
  });
  //return replaceTempFixes(new_version);
  return <span dangerouslySetInnerHTML={{ __html: new_version }} />;
}

export default function Story() {
  const hostInfo = useContext(HostInfoContext);
  const [isPopoverOpen, setPopoverOpen] = useState("");
  //The useLocation hook returns the location object that represents the current URL.
  const location = useLocation();

  // UseState Hooks to store data coming over RestAPI Call
  const [aStory, setAStory] = useState([]);
  const [storyTableData, setStoryTableData] = useState([]);
  const [Personas, setPersonas] = useState([]);

  // AlignmentMeterComponent
  const [billDiscussionLinkInfo, setBillDiscussionLinkInfo] = useState([]);

  const { httpErrorHandler, errorStatus } = useHttpErrorHandler();

  //new function by Thomas G. to be backwards compatible with old style naming.
  function check_story_id(id) {
    let tipsheet_id_fields = id.split("_");
    if (tipsheet_id_fields.length > 4 && tipsheet_id_fields[4][0] !== "T") {
      return tipsheet_id_fields[0].concat(
        "_",
        tipsheet_id_fields[1],
        "_",
        tipsheet_id_fields[3],
        "_",
        tipsheet_id_fields[4],
      );
    } else {
      return id;
    }
  }
  const APIPathStory = check_story_id(location?.pathname?.split("/")[1]);
  // API Call End Point URL for server
  const API_URL = process.env.REACT_APP_FEATURED_API + APIPathStory;

  function stringify(arr) {
    let final = "";
    arr.forEach((item) => {
      final += item;
      final += ", ";
    });
    return final.slice(0, -2);
  }

  // FormData function is used to get data for Table index on tipsheet_html_segments from Backend
  function FormData(item) {
    const rawPersonas = item.data.personas;
    const transcript = item.data.hearing_transcript;
    const hearing_id = item.data.jsonid?.split("_")[2];

    // save this to construct a link to DD hearing pages
    if (transcript)
      //.constructor === Object)
      setBillDiscussionLinkInfo(
        transcript
          ?.filter((utterance, index) => index === 0)
          .map((utterance) => {
            return {
              hearing_id: hearing_id,
              offset: utterance.start_time,
              file_id: utterance.vid_file_id,
            };
          })[0],
      );

    //filtering personas
    let cleanPersonas = [];
    if (rawPersonas) {
      rawPersonas.forEach((item) => {
        if (
          !item["p_type"].includes("Legislator") &&
          item["first"] !== "Reading" &&
          item["first"] !== "Unidentified"
        ) {
          let temp = [];
          const transcriptInfo = transcript
            ? transcript.filter(
                (utterance) => item["pid"] === utterance.speaker_pid,
              )
            : null;
          const speakerDuration = transcript
            ? transcriptInfo
                .map((line) => line.end_time - line.start_time)
                .reduce((a, v) => (a = a + v), 0)
            : null;
          temp.push({
            v: item["last"] + ", " + item["first"],
            f: LinkDD(
              item["first"] + " " + item["last"],
              item["pid"],
              "per",
              item["pid"],
              true,
            ),
          });

          if (item["p_type"].includes("Lobbyist")) {
            item["p_type"] = ["Registered Lobbyist"];
          }

          if (item["p_type"].includes("General Public")) {
            item["p_type"] = ["Advocate"];
          }

          if (item["affiliation"].includes("None")) {
            item["affiliation"] = ["[none recorded]"];
            item["p_type"] = ["General Public"];
          }

          if (Array.isArray(item["affiliation"])) {
            item["affiliation"] = item["affiliation"].join(", ");
          }

          temp.push(stringify(item["p_type"]));
          temp.push(item["affiliation"]);

          let positionValue =
            transcriptInfo && transcriptInfo?.length > 0
              ? transcriptInfo[0].alignment
              : "Undetermined";

          if (positionValue.slice(0, 3).toLowerCase() === "for") {
            item["position"] =
              `<div class="vote-icon" style="color: #386F1F;"><span>${positionValue}</span><img src=${greenCheck} alt="Green Check Icon"/></div>`;
          } else if (positionValue.slice(0, 7).toLowerCase() === "against") {
            item["position"] =
              `<div class="vote-icon" style="color: #BE2C25;"><span>${positionValue}</span><img src=${redX} alt="Red X Icon"/></div>`;
          } else {
            item["position"] =
              `<div class="vote-icon" style="color: #666666;"><span>${positionValue}</span><img src=${grayDash} alt="Gray Dash Icon"/></div>`;
          }
          temp.push(item["position"]);

          item["time"] =
            transcriptInfo && transcriptInfo?.length > 0
              ? { f: getDuration(speakerDuration), v: speakerDuration }
              : "N/A";
          item["link"] =
            transcriptInfo && transcriptInfo?.length > 0
              ? {
                  f: LinkDD(
                    "Transcript",
                    hearing_id,
                    "hea",
                    null,
                    true,
                    null,
                    "#t=" +
                      transcriptInfo[0].start_time +
                      "&f=" +
                      transcriptInfo[0].vid_file_id,
                  ),
                  v: hearing_id,
                }
              : "N/A";
          item["participation"] =
            transcriptInfo && transcriptInfo?.length > 0
              ? {
                  f:
                    getDuration(speakerDuration) +
                    LinkDD(
                      "Transcript",
                      hearing_id,
                      "hea",
                      null,
                      true,
                      null,
                      "#t=" +
                        transcriptInfo[0].start_time +
                        "&f=" +
                        transcriptInfo[0].vid_file_id,
                    ),
                  v: speakerDuration,
                }
              : "N/A";
          temp.push(item["time"]);
          temp.push(item["link"]);

          cleanPersonas.push(temp);
        }
      });
    }

    if (cleanPersonas.length > 0) {
      cleanPersonas = cleanPersonas.toSorted((a, b) => b[4]["v"] - a[4]["v"]);
    }
    setPersonas(cleanPersonas);
  }

  // fetch story data when component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchData = () => {
      let axiosT = axios.create({
        baseURL: `${API_URL}`,
        headers: {
          Authorization: `JWT ${localStorage.getItem("token")}`,
        },
      });
      axiosT
        .get("/")
        .then((response) => {
          FormData(response);
          setAStory(response.data);
        })
        .catch((err) => {
          httpErrorHandler(err);
        });
    };

    fetchData();
  }, [API_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  const pieChartData = aStory?.assets?.find(
    (type) => type.id === "speaker_participation_chart",
  )?.data;

  useEffect(() => {
    if (aStory?.assets) {
      const matchingAssets = aStory.assets.filter((asset) =>
        [
          "geo_concepts_table",
          "latest_bill_support_opposition",
          "bill_vote_history",
        ].includes(asset.id),
      );
      setStoryTableData(matchingAssets);
    }
  }, [aStory]);

  const storyTableTitle = (id) => {
    switch (id) {
      case "geo_concepts_table":
        return "Location-Specific Terms";
      case "latest_bill_support_opposition":
        return "Organization Positions on this Bill";
      case "bill_vote_history":
        return "Previous Votes on this Bill";
      default:
        return "";
    }
  };

  const alignmentData = aStory?.assets?.find(
    (type) => type.id === "vote_alignment_org_contribution_success",
  )?.data;

  const historicalAlignmentData = aStory?.assets?.find(
    (type) =>
      type.id === "vote_alignment_contribution_table_all_versions_all_time",
  )?.data;

  const donatingOrganizations = alignmentData?.filter(
    (item) => item.donations !== "N/A",
  );

  // determine whether each high‑level wrapper has any meaningful content
  const hasHeroData = aStory && Object.keys(aStory).length > 0;

  const publishYear = aStory.publish_date_str?.match(/\b(20\d{2})\b/)?.[1];
  const showCampaignFinanceWarning = hostInfo?.STATE === 'CA' && publishYear && parseInt(publishYear) < 2026;

  const hasDetailContent =
    (Personas && Personas.length > 0) ||
    storyTableData.length > 0 ||
    Boolean(pieChartData) ||
    (donatingOrganizations?.length > 1) ||
    (alignmentData && alignmentData.length > 1) ||
    (aStory.related_tipsheets?.length > 1) ||
    (historicalAlignmentData && historicalAlignmentData.length > 1);

  // build a list of jump-link sections in the order they appear
  const sections = [];

  // alignment current
  if (alignmentData && alignmentData.length > 0) {
    sections.push({
      label: "Organization Alignment",
      value: "organization-alignment",
    });
  }

  // if the alignment table is rendered it follows immediately after
  if (alignmentData && alignmentData.length > 1) {
    sections.push({
      label: "Organization Alignment Table",
      value: "organization-alignment-table",
    });
  }

  // optional governor veto message comes before quotes
  if (aStory.content?.some((p) => p.phenom === "governor_veto_message")) {
    sections.push({
      label: "Governor's Veto Message",
      value: "governor-veto-message",
    });
  }

  if (Number(aStory?.video_duration) > 180 && aStory.pullquotes?.length > 0) {
    sections.push({
      label: "Quotes From Hearing",
      value: "quotes-from-hearing",
    });
  }

  // witness table
  if (Personas && Personas.length > 0) {
    sections.push({ label: "Witnesses", value: "witnesses" });
  }

  // dynamic story tables - only include if we would render a heading (T3 pages omit the labels)
  storyTableData.forEach((table) => {
    const title = storyTableTitle(table.id);
    if (title && APIPathStory.slice(-2) !== "T3") {
      sections.push({ label: title, value: table.id });
    }
  });

  if (pieChartData) {
    sections.push({
      label: "Speaker Participation",
      value: "speaker-participation",
    });
  }

  if (donatingOrganizations?.length > 1) {
    sections.push({
      label: "Financial Relationships",
      value: "financial-relationships",
    });
  }

  if (alignmentData && alignmentData.length > 1) {
    sections.push({ label: "Alignment Meters", value: "alignment-meters" });
  }

  if (aStory.related_tipsheets?.length > 1) {
    sections.push({ label: "Related Tip Sheets", value: "related-tipsheets" });
  }

  if (historicalAlignmentData && historicalAlignmentData.length > 1) {
    sections.push({
      label: "Organization Alignment (historical)",
      value: "organization-alignment-historical",
    });
    // also link to the historical table itself
    sections.push({
      label: "Organization Alignment Table (historical)",
      value: "organization-alignment-historical-table",
    });
  }

  sections.push({ label: "Notes on Methodology", value: "notes-methodology" });

  if (errorStatus) return <div>API error, status {errorStatus}</div>;

  return (
    <>
      {hasHeroData && (
        <StoryWrapper>
          <StoryContainer>
            {showCampaignFinanceWarning && (
              <WarningBox>
                Digital Democracy in 2026 transitioned to a new campaign finance system. Campaign finance data in tipsheets from previous years may not match what currently appears on the Digital Democracy site.
              </WarningBox>
            )}
            <StoryHero
              storyData={aStory}
              prehearing={APIPathStory.slice(-2) === "T3"}
            />
          </StoryContainer>
        </StoryWrapper>
      )}
      <UtilityBar sections={sections} />
      {alignmentData && alignmentData.length > 0 && (
        <StoryWrapper>
          <StoryContainer>
            <ScrollAnchor id="organization-alignment">
              <OrganizationAlignment story={aStory} type={"current"} />
            </ScrollAnchor>
            {alignmentData.length > 1 && (
              <ScrollAnchor id="organization-alignment-table">
                <LegislatorOrgAlignmentTable
                  alignmentData={alignmentData}
                  billName={aStory?.billname}
                  type={"current"}
                />
              </ScrollAnchor>
            )}
          </StoryContainer>
        </StoryWrapper>
      )}
      {aStory.content?.some((p) => p.phenom === "governor_veto_message") && (
        <GrayWrapper>
          <ScrollAnchor id="governor-veto-message">
            <GrayContainer>
              <SectionHeading>Governor's Veto Message</SectionHeading>
              {aStory.content
                .filter((p) => p.phenom === "governor_veto_message")
                .map((phenom, index) => (
                  <div key={index}>
                    {phenom.tips?.[0]?.link_url ? (
                      <p>
                        {LinkSimple(
                          phenom.tips[0].data,
                          phenom.tips[0].link_url,
                          null,
                          false,
                        )}
                      </p>
                    ) : (
                      <>
                        <p>
                          This is the veto message pulled directly from state
                          records.
                        </p>
                        <div className="pullquote veto-message">
                          {phenom.tips[0].data
                            .replace("\n\n", "@")
                            .split("@")
                            .map((p, i) => (
                              <p key={i}>{p}</p>
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
            </GrayContainer>
          </ScrollAnchor>
        </GrayWrapper>
      )}
      {Number(aStory?.video_duration) > 180 && aStory.pullquotes?.length > 0 ? (
        <GrayWrapper>
          <ScrollAnchor id="quotes-from-hearing">
            <GrayContainer>
              <QuouteHeading>Quotes From Hearing</QuouteHeading>
              {aStory.bill_name?.toLowerCase() === "no bill discussed" ? (
                <QuoteIntro>
                  The quotes in this carousel are intended to reflect positions
                  of legislators and those who gave testimony. The quotes are
                  linked to the full transcript for context and alternate
                  quotes.
                </QuoteIntro>
              ) : (
                <QuoteIntro>
                  The quotes in this carousel are intended to reflect positions
                  for and against this bill from legislators and those who gave
                  testimony. The quotes are linked to the full transcript for
                  context and alternate quotes.
                </QuoteIntro>
              )}
              <hr />
              <Quotes
                pullquotes={aStory.pullquotes}
                jsonid={aStory.jsonid}
                vote_detail={aStory.assets?.filter(
                  (asset) => asset.id === "vote_detail",
                )}
                hearing_transcript={aStory.hearing_transcript}
                bill_authors={aStory.bill_authors}
                informational_hearing={
                  aStory.bill_name?.toLowerCase() === "no bill discussed"
                }
              />
            </GrayContainer>
          </ScrollAnchor>
        </GrayWrapper>
      ) : null}
      {hasDetailContent && (
        <StoryWrapper>
          <StoryContainer>
            {Personas && Personas.length > 0 ? (
              <ScrollAnchor id="witnesses">
                <SectionHeading>Witnesses</SectionHeading>
                <ChartTable
                  dataTable={Personas}
                  id="witnesses"
                  billName={aStory?.billname}
                />
              </ScrollAnchor>
            ) : null}

            {storyTableData.length > 0
              ? storyTableData.map((table) => (
                  <ScrollAnchor key={table.id} id={table.id}>
                    {storyTableTitle(table.id) &&
                    APIPathStory.slice(-2) !== "T3" ? (
                      <SectionHeading>
                        {storyTableTitle(table.id)}{" "}
                        <Popover
                          isOpen={isPopoverOpen === table.id}
                          positions={["top", "bottom", "left", "right"]}
                          onClickOutside={() => setPopoverOpen("")}
                          content={({ position, childRect, popoverRect }) => (
                            <ArrowContainer
                              position={position}
                              childRect={childRect}
                              popoverRect={popoverRect}
                              arrowColor={"#D9D9D9"}
                              arrowSize={16}
                            >
                              <Tooltip>
                                {table.id === "geo_concepts_table"
                                  ? "This Tip Sheet is associated with region(s) on the strength of terms listed below."
                                  : table.caption}
                              </Tooltip>
                            </ArrowContainer>
                          )}
                        >
                          <TooltipButton
                            type="button"
                            onClick={() => setPopoverOpen(table.id)}
                          >
                            <QuestionMarkIcon />
                          </TooltipButton>
                        </Popover>
                      </SectionHeading>
                    ) : null}
                    <ChartTable
                      dataTable={table.data}
                      id={table.id}
                      billName={aStory?.billname}
                    />
                  </ScrollAnchor>
                ))
              : null}

            {pieChartData ? (
              <ScrollAnchor id="speaker-participation">
                <SectionHeading>Speaker Participation</SectionHeading>
                <ChartPie
                  pieChartData={pieChartData}
                  billName={aStory?.billname}
                />
              </ScrollAnchor>
            ) : null}

            {donatingOrganizations?.length > 1 ? (
              <ScrollAnchor id="financial-relationships">
                <SectionHeading>Financial Relationships</SectionHeading>
                <SliderSankey
                  donatingOrganizations={donatingOrganizations}
                  totalIndex={2}
                  billName={aStory?.billname}
                />
              </ScrollAnchor>
            ) : null}

            {alignmentData && alignmentData?.length > 1 ? (
              <ScrollAnchor id="alignment-meters">
                <SectionHeading>Alignment Meters</SectionHeading>
                <AlignmentMeters alignmentData={alignmentData} />
              </ScrollAnchor>
            ) : null}

            {aStory.related_tipsheets?.length > 1 && (
              <ScrollAnchor id="related-tipsheets">
                <SectionHeading>
                  Related Tip Sheets
                  <Popover
                    isOpen={isPopoverOpen === "related"}
                    positions={["top", "bottom", "left", "right"]}
                    onClickOutside={() => setPopoverOpen("")}
                    content={({ position, childRect, popoverRect }) => (
                      <ArrowContainer
                        position={position}
                        childRect={childRect}
                        popoverRect={popoverRect}
                        arrowColor={"#D9D9D9"}
                        arrowSize={16}
                      >
                        <Tooltip>
                          These Tip Sheets were created for earlier votes on this
                          same bill.
                        </Tooltip>
                      </ArrowContainer>
                    )}
                  >
                    <TooltipButton
                      type="button"
                      onClick={() => setPopoverOpen("related")}
                    >
                      <QuestionMarkIcon />
                    </TooltipButton>
                  </Popover>
                </SectionHeading>
                <RenderCardsWrapper>
                  <RenderCards
                    cards={aStory.related_tipsheets
                      .filter((story) => story.id !== aStory.jsonid)
                      .toSorted(
                        (a, b) =>
                          b.tipsheet_tile_json.score - a.tipsheet_tile_json.score,
                      )}
                  />
                </RenderCardsWrapper>
              </ScrollAnchor>
            )}
            {historicalAlignmentData && historicalAlignmentData?.length > 1 && (
              <>
                <ScrollAnchor id="organization-alignment-historical">
                  <OrganizationAlignment story={aStory} type={"historical"} />
                </ScrollAnchor>
                <ScrollAnchor id="organization-alignment-historical-table">
                  <LegislatorOrgAlignmentTable
                    alignmentData={historicalAlignmentData}
                    billName={aStory?.billname}
                    type={"historical"}
                  />
                </ScrollAnchor>
              </>
            )}
            {
              <ScrollAnchor id="notes-methodology">
                <SectionHeading>Notes on Methodology</SectionHeading>
                <NotesMethodology
                  tipsheet={aStory}
                  bill={billDiscussionLinkInfo}
                />
              </ScrollAnchor>
            }
          </StoryContainer>
        </StoryWrapper>
      )}
    </>
  );
}
